import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import "./style.scss";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { BASE_URL } from "../../services/api.constant";
import { register } from "../../services/apiList";
import { useNavigate } from "react-router";
import { TypesAction } from "../../redux/typesAction";
import { theme } from "../../utils/theme";
import axios from "axios";
import { toast } from "react-toastify";
import {
  isValidEmail,
  MIN_PASSWORD_LENGTH,
} from "./constants";
import { CertificateUpload } from "./components/CertificateUpload";
import { PasswordField } from "./components/PasswordField";
import { RegisterAccountFields } from "./components/RegisterAccountFields";
import { RegisterSubmitButton } from "./components/RegisterSubmitButton";

export default function Register() {
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNew, setShowNew] = useState(true);
  const [showConfirm, setShowConfirm] = useState(true);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (certificatePreviewUrl) {
        URL.revokeObjectURL(certificatePreviewUrl);
      }
    };
  }, [certificatePreviewUrl]);

  const emailInvalid = email.length > 0 && !isValidEmail(email);
  const passwordTooShort =
    newPass.length > 0 && newPass.length < MIN_PASSWORD_LENGTH;
  const confirmMismatch =
    confirmPass.length > 0 && newPass !== confirmPass;

  const canSubmit = useMemo(
    () =>
      companyName.trim() !== "" &&
      email.trim() !== "" &&
      fullname.trim() !== "" &&
      phoneNumber.trim() !== "" &&
      address.trim() !== "" &&
      confirmPass !== "" &&
      newPass.length >= MIN_PASSWORD_LENGTH &&
      isValidEmail(email) &&
      newPass === confirmPass,
    [
      companyName,
      email,
      fullname,
      phoneNumber,
      address,
      confirmPass,
      newPass,
    ]
  );

  const handleCertificateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertificateFile(file);
    setCertificatePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleRegister = async () => {
    setLoading(true);
    const formData = new FormData();
    if (certificateFile) {
      formData.append("certificate", certificateFile);
    } else {
      formData.append("certificate", "");
    }
    formData.append("email", email);
    formData.append("name", fullname);
    formData.append("full_name", fullname);
    formData.append("company_name", companyName);
    formData.append("address", address);
    formData.append("phone_number", phoneNumber);
    formData.append("password", newPass);

    try {
      const response = await axios({
        method: "post",
        url: `${BASE_URL}${register()}`,
        data: formData,
      });
      if (response.status === 200) {
        dispatch({
          type: TypesAction.VERIFYOTP,
          payload: response.data.data.email,
        });
        navigate("/validateOtp", { state: 1 });
      }
    } catch {
      toast.error("The email has already been taken.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        marginBottom: 7,
        padding: 0,
      }}>
      <Box className="bg_box_register">
        <Typography
          sx={{
            fontSize: 25,
            textAlign: "center",
            color: `${theme.palette.text.body}`,
            fontWeight: 600,
          }}>
          REGISTER
        </Typography>

        <CertificateUpload
          hasFile={Boolean(certificateFile)}
          previewUrl={certificatePreviewUrl}
          onFileChange={handleCertificateChange}
        />

        <RegisterAccountFields
          loading={loading}
          companyName={companyName}
          onCompanyNameChange={setCompanyName}
          email={email}
          onEmailChange={setEmail}
          emailInvalid={emailInvalid}
          fullname={fullname}
          onFullnameChange={setFullname}
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          address={address}
          onAddressChange={setAddress}
        />

        <PasswordField
          label="CREATE NEW PASSWORD"
          value={newPass}
          onChange={setNewPass}
          disabled={loading}
          showPassword={showNew}
          onToggleVisibility={() => setShowNew((s) => !s)}
          error={passwordTooShort}
          color={passwordTooShort ? "error" : "success"}
          helperText="The password must be at least 8 characters"
          placeholder="New password"
        />

        <PasswordField
          label="CONFIRM NEW PASSWORD"
          value={confirmPass}
          onChange={setConfirmPass}
          disabled={loading}
          showPassword={showConfirm}
          onToggleVisibility={() => setShowConfirm((s) => !s)}
          error={confirmMismatch}
          color={confirmMismatch ? "error" : "success"}
          helperText="Password does not match."
          placeholder="Confirm new password"
        />

        <RegisterSubmitButton
          loading={loading}
          disabled={!canSubmit}
          onRegister={handleRegister}
        />
      </Box>
    </Container>
  );
}
